import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Chip, useTheme, alpha, ToggleButton, ToggleButtonGroup,
  TextField, InputAdornment,
} from '@mui/material';
import {
  Cake as BirthIcon,
  Close as DeathIcon,
  Favorite as UnionIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Membre, Relation } from '../types';

interface Props {
  membres: Membre[];
  relations: Relation[];
  onSelectMember: (id: number) => void;
}

type EventType = 'naissance' | 'deces' | 'union';

interface TimelineEvent {
  year: number;
  date: string;
  type: EventType;
  label: string;
  memberId: number;
  memberId2?: number;
  sexe?: string;
}

const TYPE_CONFIG: Record<EventType, { icon: React.ReactNode; color: string; label: string }> = {
  naissance: { icon: <BirthIcon sx={{ fontSize: 14 }} />, color: '#10B981', label: 'Naissances' },
  deces:     { icon: <DeathIcon  sx={{ fontSize: 14 }} />, color: '#6B7280', label: 'Décès' },
  union:     { icon: <UnionIcon  sx={{ fontSize: 14 }} />, color: '#F87171', label: 'Unions' },
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const Timeline: React.FC<Props> = ({ membres, relations, onSelectMember }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [filter, setFilter] = useState<EventType | 'all'>('all');
  const [search, setSearch] = useState('');

  const events = useMemo<TimelineEvent[]>(() => {
    const evts: TimelineEvent[] = [];

    membres.forEach(m => {
      const fullName = `${m.prenom} ${m.nom}`;
      if (m.date_naissance) {
        evts.push({
          year: new Date(m.date_naissance).getFullYear(),
          date: m.date_naissance,
          type: 'naissance',
          label: `Naissance de ${fullName}`,
          memberId: m.id,
          sexe: m.sexe,
        });
      }
      if (m.date_deces) {
        evts.push({
          year: new Date(m.date_deces).getFullYear(),
          date: m.date_deces,
          type: 'deces',
          label: `Décès de ${fullName}`,
          memberId: m.id,
          sexe: m.sexe,
        });
      }
    });

    const processed = new Set<string>();
    relations.filter(r => ['conjoint', 'ex_conjoint'].includes(r.type_relation)).forEach(r => {
      const key = [Math.min(r.membre_source, r.membre_cible), Math.max(r.membre_source, r.membre_cible)].join('-');
      if (processed.has(key)) return;
      processed.add(key);
      const m1 = membres.find(m => m.id === r.membre_source);
      const m2 = membres.find(m => m.id === r.membre_cible);
      if (!m1 || !m2) return;
      const label = r.type_relation === 'conjoint'
        ? `Union de ${m1.prenom} ${m1.nom} & ${m2.prenom} ${m2.nom}`
        : `Ex-union de ${m1.prenom} ${m1.nom} & ${m2.prenom} ${m2.nom}`;
      evts.push({
        year: 0,
        date: '',
        type: 'union',
        label,
        memberId: r.membre_source,
        memberId2: r.membre_cible,
      });
    });

    return evts.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.localeCompare(b.date);
    });
  }, [membres, relations]);

  const filtered = useMemo(() => {
    let evts = filter === 'all' ? events : events.filter(e => e.type === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      evts = evts.filter(e => e.label.toLowerCase().includes(q));
    }
    return evts;
  }, [events, filter, search]);

  // Grouper par décennie
  const groups = useMemo(() => {
    const map = new Map<string, TimelineEvent[]>();
    filtered.forEach(e => {
      const key = e.date
        ? `${Math.floor(e.year / 10) * 10}s`
        : 'Unions (date inconnue)';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return map;
  }, [filtered]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: isDark ? 'background.default' : '#F9FAF5' }}>
      <Box sx={{ maxWidth: 780, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>Chronologie</Typography>
          <TextField
            size="small" placeholder="Rechercher…" value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
          />
          <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
            <ToggleButton value="all" sx={{ px: 1.5, fontSize: '0.75rem' }}>Tout</ToggleButton>
            {(Object.entries(TYPE_CONFIG) as [EventType, typeof TYPE_CONFIG[EventType]][]).map(([type, cfg]) => (
              <ToggleButton key={type} value={type} sx={{ px: 1.5, gap: 0.5, fontSize: '0.75rem' }}>
                {cfg.icon} {cfg.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {filtered.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.disabled">Aucun événement trouvé</Typography>
          </Box>
        )}

        {Array.from(groups.entries()).map(([decade, evts]) => (
          <Box key={decade} sx={{ mb: 3 }}>
            {/* Decade header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Typography variant="caption" fontWeight={800} color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                {decade}
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
              <Chip label={evts.length} size="small" sx={{ height: 18, fontSize: '0.65rem', opacity: 0.6 }} />
            </Box>

            {/* Events */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {evts.map((evt, i) => {
                const cfg = TYPE_CONFIG[evt.type];
                return (
                  <Box
                    key={i}
                    onClick={() => onSelectMember(evt.memberId)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.2, borderRadius: 2, cursor: 'pointer',
                      border: `1px solid transparent`,
                      '&:hover': {
                        bgcolor: alpha(cfg.color, 0.08),
                        border: `1px solid ${alpha(cfg.color, 0.25)}`,
                      },
                      transition: 'all 0.15s',
                    }}
                  >
                    {/* Icon */}
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      bgcolor: alpha(cfg.color, 0.12), color: cfg.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {cfg.icon}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap fontSize="0.85rem">
                        {evt.label}
                      </Typography>
                      {evt.date && (
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(evt.date)}
                        </Typography>
                      )}
                    </Box>

                    {evt.date && (
                      <Chip
                        label={evt.year}
                        size="small"
                        sx={{ height: 20, fontSize: '0.68rem', bgcolor: alpha(cfg.color, 0.1), color: cfg.color, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Timeline;
